import { useState, useEffect, useRef } from "react";
import HighlightOff from "@mui/icons-material/HighlightOff";
import AutoModeIcon from "@mui/icons-material/AutoMode";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUp from "@mui/icons-material/KeyboardArrowUp";
import MoreHoriz from "@mui/icons-material/MoreHoriz";
import Tooltip from "@mui/material/Tooltip";
import { DetailSidePanel } from "./DetailSidePanel";
import "./bulk-upload.css";

export function DataTable({
  tabId,
  records,
  selectedIds,
  onSelectionChange,
  isLocked,
  currentPage,
  setCurrentPage,
  accordionSelected,
  setAccordionSelected,
  showAccordion,
  onMoveRecord,
  onVendorSearch,
  isCountryEligibleForVendorSearch,
  clearSelectionsRequest,
  headerSelectionRequest,
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [accordionFilter, setAccordionFilter] = useState("all");
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [expandedHcpAccordions, setExpandedHcpAccordions] = useState(new Set());
  const [expandedHcoAccordions, setExpandedHcoAccordions] = useState(new Set());
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [openedRecordIds, setOpenedRecordIds] = useState(new Set());
  const [explicitReviewedRecordIds, setExplicitReviewedRecordIds] = useState(
    new Set(),
  );
  const [clearedAutoSelectionKeys, setClearedAutoSelectionKeys] = useState(
    new Set(),
  );
  const lastHandledClearRequestId = useRef({});
  const lastHandledHeaderSelectionRequestId = useRef({});
  const skipNextAutoSelectionRef = useRef(false);
  const recordsPerPage = 10;
  const previewFieldCount = 5;

  const ENTITY_FIELD_CONFIG = {
    HCP: {
      label: "HCP",
      fields: [
        ["enterprise_id", "Enterprise ID"],
        ["country_code", "Country"],
        ["first_name", "First Name"],
        ["middle_name", "Middle Name"],
        ["last_name", "Last Name"],
        ["secondary_last_name", "Secondary Last Name"],
        ["prefix", "Prefix"],
        ["classification_code", "Classification Code"],
        ["active_flag", "Active Flag"],
        ["primary_specialty", "Primary Specialty"],
        ["specialty_2", "Specialty 2"],
        ["specialty_3", "Specialty 3"],
        ["phone", "Phone"],
        ["email", "Email"],
        ["identifier_1_type", "Identifier 1 Type"],
        ["identifier_1_id", "Identifier 1 ID"],
        ["identifier_2_type", "Identifier 2 Type"],
        ["identifier_2_id", "Identifier 2 ID"],
        ["identifier_3_type", "Identifier 3 Type"],
        ["identifier_3_id", "Identifier 3 ID"],
        ["hcp_adrs_source_adrs_id", "Address Source ID"],
        ["hcp_adrs_line_1", "Address Line 1"],
        ["hcp_adrs_line_2", "Address Line 2"],
        ["hcp_adrs_city", "City"],
        ["hcp_adrs_state_code", "State"],
        ["hcp_adrs_country", "Address Country"],
        ["hcp_adrs_zip", "ZIP"],
        ["hcp_adrs_rank", "Address Rank"],
      ],
    },
    HCO: {
      label: "HCO",
      fields: [
        ["enterprise_id", "Enterprise ID"],
        ["country_code", "Country"],
        ["name", "Name"],
        ["classification_code", "Classification Code"],
        ["active_flag", "Active Flag"],
        ["primary_specialty", "Primary Specialty"],
        ["specialty_2", "Specialty 2"],
        ["specialty_3", "Specialty 3"],
        ["phone", "Phone"],
        ["email", "Email"],
        ["source_address_id", "Address Source ID"],
        ["hco_adrs_line_1", "Address Line 1"],
        ["hco_adrs_city", "City"],
        ["hco_adrs_state_code", "State"],
        ["hco_adrs_country", "Address Country"],
        ["hco_adrs_rank", "Address Rank"],
        ["hco_adrs_zip", "ZIP"],
        ["identifier_1_type", "Identifier 1 Type"],
        ["identifier_1_id", "Identifier 1 ID"],
        ["identifier_2_type", "Identifier 2 Type"],
        ["identifier_2_id", "Identifier 2 ID"],
        ["identifier_3_type", "Identifier 3 Type"],
        ["identifier_3_id", "Identifier 3 ID"],
        ["rel_hco_enterprise_id", "Enterprise ID"],
        ["rel_hco_name", "Name"],
        ["rel_hco_clsfctn_cd", "Classification Code"],
        ["rel_hco_country_code", "Country"],
        ["rel_hco_adrs_line_1", "Address Line 1"],
        ["rel_hco_adrs_line_2", "Address Line 2"],
        ["rel_hco_adrs_city", "City"],
        ["rel_hco_adrs_state_code", "State"],
        ["rel_hco_adrs_country", "Address Country"],
        ["rel_hco_adrs_zip", "ZIP"],
      ],
    },
    PARENT_HCO: {
      label: "Parent HCO",
      fields: [
        ["enterprise_id", "Enterprise ID"],
        ["country_code", "Country"],
        ["name", "Name"],
        ["classification_code", "Classification Code"],
        ["active_flag", "Active Flag"],
        ["primary_specialty", "Primary Specialty"],
        ["specialty_2", "Specialty 2"],
        ["specialty_3", "Specialty 3"],
        ["phone", "Phone"],
        ["email", "Email"],
        ["source_address_id", "Address Source ID"],
        ["hco_adrs_line_1", "Address Line 1"],
        ["hco_adrs_city", "City"],
        ["hco_adrs_state_code", "State"],
        ["hco_adrs_country", "Address Country"],
        ["hco_adrs_rank", "Address Rank"],
        ["hco_adrs_zip", "ZIP"],
        ["identifier_1_type", "Identifier 1 Type"],
        ["identifier_1_id", "Identifier 1 ID"],
        ["identifier_2_type", "Identifier 2 Type"],
        ["identifier_2_id", "Identifier 2 ID"],
        ["identifier_3_type", "Identifier 3 Type"],
        ["identifier_3_id", "Identifier 3 ID"],
        ["rel_hco_enterprise_id", "Enterprise ID"],
        ["rel_hco_name", "Name"],
        ["rel_hco_clsfctn_cd", "Classification Code"],
        ["rel_hco_country_code", "Country"],
        ["rel_hco_adrs_line_1", "Address Line 1"],
        ["rel_hco_adrs_line_2", "Address Line 2"],
        ["rel_hco_adrs_city", "City"],
        ["rel_hco_adrs_state_code", "State"],
        ["rel_hco_adrs_country", "Address Country"],
        ["rel_hco_adrs_zip", "ZIP"],
      ],
    },
  };

  const normalizeFieldValue = (value) => {
    if (value === undefined || value === null) {
      return "";
    }
    if (Array.isArray(value)) {
      return value.filter(Boolean).join(", ");
    }
    return String(value).trim();
  };

  const compactValue = (value, maxLength = 26) => {
    const text = normalizeFieldValue(value);
    if (text.length <= maxLength) {
      return text;
    }
    return `${text.slice(0, maxLength)}...`;
  };

  // Close side panel when tab changes
  useEffect(() => {
    setIsSidePanelOpen(false);
    setSelectedRecord(null);

    // Reset local filters so one tab's filter does not hide another tab's data.
    setSearchQuery("");
    setStatusFilter("all");
    setAccordionFilter("all");
  }, [tabId]);

  useEffect(() => {
    if (!selectedRecord?.id) {
      return;
    }

    const latestSelectedRecord = records.find(
      (record) => record.id === selectedRecord.id,
    );

    if (latestSelectedRecord && latestSelectedRecord !== selectedRecord) {
      setSelectedRecord(latestSelectedRecord);
    }
  }, [records, selectedRecord]);

  const toggleRow = (id) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const toggleHcpAccordion = (id) => {
    const newExpanded = new Set(expandedHcpAccordions);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedHcpAccordions(newExpanded);
  };

  const toggleHcoAccordion = (id) => {
    const newExpanded = new Set(expandedHcoAccordions);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedHcoAccordions(newExpanded);
  };

  const handleAccordionRadio = (subRecordId, parentId, type) => {
    const key = `${parentId}-${type}`;
    const newAccordionSelected = { ...accordionSelected };
    newAccordionSelected[key] = subRecordId;
    setAccordionSelected(newAccordionSelected);

    // Auto-check main checkbox
    const newSelected = new Set(selectedIds);
    newSelected.add(parentId);
    onSelectionChange(newSelected);
  };

  const filteredRecords = records.filter((record) => {
    const matchesSearch =
      record.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      record.status.toLowerCase().includes(searchQuery.toLowerCase()) ||
      record.country.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (record.enterpriseId || "NA")
        .toLowerCase()
        .includes(searchQuery.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || record.status === statusFilter;

    const matchesAccordion =
      accordionFilter === "all" ||
      (accordionFilter === "withAccordion" && record.hasAccordion) ||
      (accordionFilter === "withoutAccordion" && !record.hasAccordion);

    return matchesSearch && matchesStatus && matchesAccordion;
  });

  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);
  const startIndex = (currentPage - 1) * recordsPerPage;
  const paginatedRecords = filteredRecords.slice(
    startIndex,
    startIndex + recordsPerPage,
  );

  // Keep page in valid range so existing data does not appear as blank.
  useEffect(() => {
    if (totalPages === 0 && currentPage !== 1) {
      setCurrentPage(1);
      return;
    }
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages, setCurrentPage]);

  const handleSelectAll = () => {
    // Only disable "Not Found" status records in EPH and Vendor tabs, not in the Not Found tab
    const selectableRecords =
      tabId === "notfound"
        ? paginatedRecords
        : paginatedRecords.filter((r) => r.status !== "Not Found");
    const selectableIds = selectableRecords.map((r) => r.id);
    const allSelectableSelected = selectableIds.every((id) =>
      selectedIds.has(id),
    );

    if (allSelectableSelected && selectableIds.length > 0) {
      onSelectionChange(new Set());
      // Clear all radio selections for deselected records
      const newAccordionSelected = { ...accordionSelected };
      selectableIds.forEach((id) => {
        delete newAccordionSelected[`${id}-hcp`];
        delete newAccordionSelected[`${id}-hco`];
        delete newAccordionSelected[`${id}-parentHco`];
      });
      setAccordionSelected(newAccordionSelected);
      setClearedAutoSelectionKeys((prev) => {
        const next = new Set(prev);
        selectableIds.forEach((id) => {
          next.add(`${id}-hcp`);
          next.add(`${id}-hco`);
          next.add(`${id}-parentHco`);
        });
        return next;
      });
    } else {
      onSelectionChange(new Set(selectableIds));
      setClearedAutoSelectionKeys((prev) => {
        const next = new Set(prev);
        selectableIds.forEach((id) => {
          next.delete(`${id}-hcp`);
          next.delete(`${id}-hco`);
          next.delete(`${id}-parentHco`);
        });
        return next;
      });
    }
  };

  const handleSelectRecord = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
      const newAccordionSelected = { ...accordionSelected };
      delete newAccordionSelected[`${id}-hcp`];
      delete newAccordionSelected[`${id}-hco`];
      delete newAccordionSelected[`${id}-parentHco`];
      setAccordionSelected(newAccordionSelected);
      setClearedAutoSelectionKeys((prev) => {
        const next = new Set(prev);
        next.add(`${id}-hcp`);
        next.add(`${id}-hco`);
        next.add(`${id}-parentHco`);
        return next;
      });
      setExplicitReviewedRecordIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } else {
      newSelected.add(id);
      setClearedAutoSelectionKeys((prev) => {
        const next = new Set(prev);
        next.delete(`${id}-hcp`);
        next.delete(`${id}-hco`);
        next.delete(`${id}-parentHco`);
        return next;
      });
    }
    onSelectionChange(newSelected);
  };

  const selectableRecords =
    tabId === "notfound"
      ? paginatedRecords
      : paginatedRecords.filter((r) => r.status !== "Not Found");
  const isAllSelected =
    selectableRecords.length > 0 &&
    selectableRecords.every((r) => selectedIds.has(r.id));

  const handleRowClick = (record) => {
    setSelectedRecord(record);
    setOpenedRecordIds((prev) => new Set([...prev, record.id]));

    const autoSelections = getAutoAccordionSelections(record);
    if (autoSelections) {
      const nextAccordionSelected = { ...accordionSelected };
      let accordionChanged = false;
      let hasEligibleAutoSelection = false;

      Object.entries(autoSelections).forEach(([key, value]) => {
        if (clearedAutoSelectionKeys.has(key)) {
          return;
        }
        if (value && nextAccordionSelected[key] !== value) {
          nextAccordionSelected[key] = value;
          accordionChanged = true;
        }
        if (value) {
          hasEligibleAutoSelection = true;
        }
      });

      if (accordionChanged) {
        setAccordionSelected(nextAccordionSelected);
      }

      if (
        hasEligibleAutoSelection &&
        !selectedIds.has(record.id)
      ) {
        const nextSelected = new Set(selectedIds);
        nextSelected.add(record.id);
        onSelectionChange(nextSelected);
      }
    }

    if (tabId !== "notfound") {
      setIsSidePanelOpen(true);
    }
  };

  const handleAccordionSelect = (recordId, accordionId, type) => {
    const key = `${recordId}-${type}`;
    const newAccordionSelected = { ...accordionSelected };

    if (!accordionId) {
      // Clear selected result for only one tab (hcp/hco)
      delete newAccordionSelected[key];
      setAccordionSelected(newAccordionSelected);
      setClearedAutoSelectionKeys((prev) => new Set(prev).add(key));
      setExplicitReviewedRecordIds((prev) => {
        const next = new Set(prev);
        next.delete(recordId);
        return next;
      });

      const hasAnySelectionForRecord =
        !!newAccordionSelected[`${recordId}-hcp`] ||
        !!newAccordionSelected[`${recordId}-hco`] ||
        !!newAccordionSelected[`${recordId}-parentHco`];

      if (!hasAnySelectionForRecord) {
        const newSelected = new Set(selectedIds);
        newSelected.delete(recordId);
        onSelectionChange(newSelected);
      }
      return;
    }

    // Check the main checkbox when a result is selected
    const newSelected = new Set(selectedIds);
    newSelected.add(recordId);
    onSelectionChange(newSelected);

    // Update accordion selection
    newAccordionSelected[key] = accordionId;
    setAccordionSelected(newAccordionSelected);
    setClearedAutoSelectionKeys((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
    setExplicitReviewedRecordIds((prev) => {
      const next = new Set(prev);
      next.add(recordId);
      return next;
    });
  };

  const dedupeByIdentity = (items) => {
    const seen = new Set();
    return items.filter((item) => {
      const key = item.id || item.enterpriseId || item.name;
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const findSelectedAccordionRecord = (record, entityKey, selectedId) => {
    if (!record || !selectedId) {
      return null;
    }

    const candidates = [
      ...(record?.accordionData?.[entityKey] || []),
      ...(record?.ephAccordionData?.[entityKey] || []),
      ...(record?.vendorAccordionData?.[entityKey] || []),
    ];

    return candidates.find((item) => item.id === selectedId) || null;
  };

  const getSelectedResultDisplayId = (entityRecord) => {
    if (!entityRecord) {
      return "";
    }
    return entityRecord.enterpriseId || entityRecord.id || "";
  };

  const buildHcpPreview = (record, selectedHcpId, selectedHcoId) => {
    const sourceHcpsRaw =
      record.accordionData?.hcp?.length > 0
        ? record.accordionData.hcp
        : (record.subRecords || []).filter((item) => item.type === "HCP");
    const sourceHcosRaw =
      record.accordionData?.hco?.length > 0
        ? record.accordionData.hco
        : (record.subRecords || []).filter((item) => item.type === "HCO");
    const sourceHcps = dedupeByIdentity(sourceHcpsRaw);
    const sourceHcos = dedupeByIdentity(sourceHcosRaw);
    const parentHco =
      sourceHcos.find((item) => item.id === selectedHcoId) || sourceHcos[0];

    const normalizedHcps =
      sourceHcps.length > 0
        ? sourceHcps
        : [
            {
              id: `${record.id}-hcp-fallback`,
              enterpriseId: `${record.enterpriseId || "ENT"}-HCP`,
              name:
                record.details?.firstName || record.details?.lastName
                  ? `${record.details?.firstName || ""} ${record.details?.lastName || ""}`.trim()
                  : record.name || "Unknown HCP",
              country: record.country || "N/A",
              status: record.status || "N/A",
            },
          ];

    const sortedHcps = [...normalizedHcps];
    if (selectedHcpId) {
      sortedHcps.sort((a, b) => {
        if (a.id === selectedHcpId) return -1;
        if (b.id === selectedHcpId) return 1;
        return 0;
      });
    }

    return sortedHcps.slice(0, 2).map((hcp, idx) => {
      const hcpNameParts = (hcp.name || "").replace("Dr. ", "").split(" ");
      const firstName =
        hcp.firstName || hcpNameParts[0] || record.details?.firstName || "N/A";
      const lastName =
        hcp.lastName ||
        (hcpNameParts.length > 1
          ? hcpNameParts[hcpNameParts.length - 1]
          : record.details?.lastName || "N/A");
      const middleName = hcp.middleName || record.details?.middleName || "";
      const parentHcoName = parentHco?.name || "N/A";
      const parentHcoHover = [
        `Parent HCO Enterprise ID: ${parentHco?.enterpriseId || "N/A"}`,
        `Parent HCO Name: ${parentHco?.name || "N/A"}`,
        `Parent HCO Country: ${parentHco?.country || record.country || "N/A"}`,
        `Parent HCO Status: ${parentHco?.status || record.status || "N/A"}`,
      ].join("\n");

      return {
        id: hcp.id || `${record.id}-hcp-preview-${idx}`,
        enterpriseId: hcp.enterpriseId || "NA",
        firstName: firstName || "N/A",
        lastName,
        middleName,
        classificationCode:
          hcp.classificationCode || record.classificationCode || "N/A",
        primarySpecialty: hcp.primarySpecialty || "N/A",
        parentHcoName,
        parentHcoHover,
        isReviewed: hcp.id === selectedHcpId,
      };
    });
  };

  const buildHcoPreview = (record, selectedHcoId) => {
    const sourceHcosRaw =
      record.accordionData?.hco?.length > 0
        ? record.accordionData.hco
        : (record.subRecords || []).filter((item) => item.type === "HCO");
    const sourceHcos = dedupeByIdentity(sourceHcosRaw);

    const normalizedHcos =
      sourceHcos.length > 0
        ? sourceHcos
        : [
            {
              id: `${record.id}-hco-fallback`,
              enterpriseId: `${record.enterpriseId || "ENT"}-HCO`,
              name: `${record.name || "Unknown"} Organization`,
              country: record.country || "N/A",
              status: record.status || "N/A",
            },
          ];

    const sortedHcos = [...normalizedHcos];
    if (selectedHcoId) {
      sortedHcos.sort((a, b) => {
        if (a.id === selectedHcoId) return -1;
        if (b.id === selectedHcoId) return 1;
        return 0;
      });
    }

    return sortedHcos.slice(0, 2).map((hco, idx) => {
      const hoverDetails = [
        `Enterprise ID: ${hco.enterpriseId || "NA"}`,
        `Name: ${hco.name || "N/A"}`,
        `Country: ${hco.country || record.country || "N/A"}`,
        `Status: ${hco.status || record.status || "N/A"}`,
        `Identifier Type: ${record.details?.identifierType || "N/A"}`,
        `Identifier ID: ${record.details?.identifierId || "N/A"}`,
      ].join("\n");

      return {
        id: hco.id || `${record.id}-hco-preview-${idx}`,
        enterpriseId: hco.enterpriseId || "NA",
        name: hco.name || "N/A",
        classificationCode:
          hco.classificationCode || record.classificationCode || "N/A",
        hoverDetails,
        isReviewed: hco.id === selectedHcoId,
      };
    });
  };

  const buildEntitySummaryFromInput = (entityType, entity) => {
    if (!entity) {
      return null;
    }

    const config = ENTITY_FIELD_CONFIG[entityType];
    if (!config) {
      return null;
    }

    const availableLines = config.fields
      .map(([key, label]) => {
        const value = normalizeFieldValue(entity?.[key]);
        return value ? { key: label, value } : null;
      })
      .filter(Boolean);

    if (availableLines.length === 0) {
      return null;
    }

    const hoverDetails = availableLines
      .map((line) => `${line.key}: ${line.value}`)
      .join("\n");

    return {
      label: config.label,
      lines: availableLines.slice(0, previewFieldCount),
      hoverDetails,
      hiddenCount: Math.max(0, availableLines.length - previewFieldCount),
    };
  };

  const buildDefaultSummaryColumns = (primaryHcp, primaryHco) => ({
    left: {
      label: "HCP",
      lines: [
        { key: "Enterprise ID", value: primaryHcp?.enterpriseId || "NA" },
        {
          key: "Name",
          value:
            `${primaryHcp?.firstName || ""} ${primaryHcp?.middleName ? `${primaryHcp.middleName} ` : ""}${primaryHcp?.lastName || ""}`
              .replace(/\s+/g, " ")
              .trim() || "N/A",
        },
        {
          key: "Classification Code",
          value: primaryHcp?.classificationCode || "N/A",
        },
        {
          key: "Primary Specialty",
          value: primaryHcp?.primarySpecialty || "N/A",
        },
      ],
    },
    right: {
      label: "HCO",
      lines: [
        { key: "Enterprise ID", value: primaryHco?.enterpriseId || "NA" },
        { key: "Name", value: primaryHco?.name || "N/A" },
        {
          key: "Classification Code",
          value: primaryHco?.classificationCode || "N/A",
        },
      ],
    },
  });

  const hasEntityInputForSelection = (entity) =>
    !!entity && typeof entity === "object" && Object.keys(entity).length > 0;

  const isUniqueResultRecord = (record) => {
    const inputEntities = record?.inputEntities || {};
    const hasInputHcp = hasEntityInputForSelection(inputEntities.hcp);
    const hasInputHco = hasEntityInputForSelection(inputEntities.hco);
    const hasInputParentHco = hasEntityInputForSelection(
      inputEntities.parentHco,
    );

    const hcpResultCount = record?.accordionData?.hcp?.length || 0;
    const hcoResultCount = record?.accordionData?.hco?.length || 0;
    const parentHcoResultCount = record?.accordionData?.parentHco?.length || 0;

    const isSingleHcpLayout = hasInputHcp && !hasInputHco && !hasInputParentHco;
    const isSingleHcoLayout = hasInputHco && !hasInputHcp && !hasInputParentHco;
    const isHcoParentLayout = hasInputHco && hasInputParentHco && !hasInputHcp;
    const isHcpParentLayout = hasInputHcp && hasInputParentHco && !hasInputHco;

    const useHcoOnLeft = isSingleHcoLayout || isHcoParentLayout;
    const showRightTabForLayout = !(isSingleHcpLayout || isSingleHcoLayout);
    const useParentHcoOnRight = isHcoParentLayout || isHcpParentLayout;

    const leftRenderedCount = useHcoOnLeft ? hcoResultCount : hcpResultCount;
    const rightRenderedCount = useParentHcoOnRight
      ? parentHcoResultCount
      : hcoResultCount;

    return showRightTabForLayout
      ? leftRenderedCount === 1 && rightRenderedCount === 1
      : leftRenderedCount === 1;
  };

  const getDcrActionMessage = ({
    hasInputHcp,
    hasInputHco,
    hasInputParentHco,
    isHcpSelected,
    isHcoSelected,
    isParentHcoSelected,
  }) => {
    // Two-tab HCP + Parent HCO combinations
    if (hasInputHcp && hasInputParentHco && !hasInputHco) {
      if (isHcpSelected && isParentHcoSelected) {
        return "Creates a DCR for Existing HCP with Existing Parent HCO";
      }
      if (!isHcpSelected && !isParentHcoSelected) {
        return "Creates a DCR for New HCP with New Parent HCO";
      }
      if (isHcpSelected && !isParentHcoSelected) {
        return "Creates a DCR for Existing HCP with New Parent HCO";
      }
      return "Creates a DCR for New HCP with Existing Parent HCO";
    }

    // Two-tab HCO + Parent HCO combinations
    if (hasInputHco && hasInputParentHco) {
      if (isHcoSelected && isParentHcoSelected) {
        return "Creates a DCR for Existing HCO with Existing Parent HCO";
      }
      if (!isHcoSelected && !isParentHcoSelected) {
        return "Creates a DCR for New HCO with New Parent HCO";
      }
      if (isHcoSelected && !isParentHcoSelected) {
        return "Creates a DCR for Existing HCO with New Parent HCO";
      }
      return "Creates a DCR for New HCO with Existing Parent HCO";
    }

    // One-tab HCP
    if (hasInputHcp && !hasInputHco && !hasInputParentHco) {
      return isHcpSelected
        ? "Creates a DCR for Edit HCP"
        : "Creates a DCR for New HCP";
    }

    // One-tab HCO
    if (hasInputHco && !hasInputHcp && !hasInputParentHco) {
      return isHcoSelected
        ? "Creates a DCR for Edit HCO"
        : "Creates a DCR for New HCO";
    }

    return "";
  };

  const getAutoAccordionSelections = (record) => {
    if (!record) {
      return null;
    }

    const inputEntities = record?.inputEntities || {};
    const hasInputHcp = hasEntityInputForSelection(inputEntities.hcp);
    const hasInputHco = hasEntityInputForSelection(inputEntities.hco);
    const hasInputParentHco = hasEntityInputForSelection(
      inputEntities.parentHco,
    );
    const isSingleHcpLayout = hasInputHcp && !hasInputHco && !hasInputParentHco;
    const isSingleHcoLayout = hasInputHco && !hasInputHcp && !hasInputParentHco;
    const isHcoParentLayout = hasInputHco && hasInputParentHco && !hasInputHcp;
    const isHcpParentLayout = hasInputHcp && hasInputParentHco && !hasInputHco;

    const useHcoOnLeft = isSingleHcoLayout || isHcoParentLayout;
    const showRightTabForLayout = !(isSingleHcpLayout || isSingleHcoLayout);
    const useParentHcoOnRight = isHcoParentLayout || isHcpParentLayout;

    const leftSelectionType = useHcoOnLeft ? "hco" : "hcp";
    const rightSelectionType = useParentHcoOnRight ? "parentHco" : "hco";
    const leftData = useHcoOnLeft
      ? record?.accordionData?.hco || []
      : record?.accordionData?.hcp || [];
    const rightData = useParentHcoOnRight
      ? record?.accordionData?.parentHco || []
      : record?.accordionData?.hco || [];

    const nextSelections = {};
    if (leftData.length === 1 && leftData[0]?.id) {
      nextSelections[`${record.id}-${leftSelectionType}`] = leftData[0].id;
    }
    if (showRightTabForLayout && rightData.length === 1 && rightData[0]?.id) {
      nextSelections[`${record.id}-${rightSelectionType}`] = rightData[0].id;
    }

    return Object.keys(nextSelections).length > 0 ? nextSelections : null;
  };

  const isRecordAutoSelected = (record, accordionMap) => {
    const autoSelections = getAutoAccordionSelections(record);
    const autoKeys = Object.keys(autoSelections || {}).filter(
      (key) => autoSelections[key],
    );

    if (autoKeys.length === 0) {
      return false;
    }

    const currentKeys = ["hcp", "hco", "parentHco"]
      .map((type) => `${record.id}-${type}`)
      .filter((key) => !!accordionMap?.[key]);

    if (currentKeys.length !== autoKeys.length) {
      return false;
    }

    return autoKeys.every((key) => accordionMap?.[key] === autoSelections[key]);
  };

  useEffect(() => {
    const requestId = clearSelectionsRequest?.id;
    const mode = clearSelectionsRequest?.mode;

    if (!requestId || !mode || requestId === lastHandledClearRequestId.current[tabId]) {
      return;
    }

    lastHandledClearRequestId.current[tabId] = requestId;

    const removeRecordSelections = (idsToClear) => {
      if (!idsToClear || idsToClear.length === 0) {
        return;
      }

      const idsToClearSet = new Set(idsToClear);
      const nextSelected = new Set(
        [...selectedIds].filter((id) => !idsToClearSet.has(id)),
      );

      const nextAccordionSelected = { ...accordionSelected };
      idsToClear.forEach((id) => {
        delete nextAccordionSelected[`${id}-hcp`];
        delete nextAccordionSelected[`${id}-hco`];
        delete nextAccordionSelected[`${id}-parentHco`];
      });

      onSelectionChange(nextSelected);
      setAccordionSelected(nextAccordionSelected);

      setClearedAutoSelectionKeys((prev) => {
        const next = new Set(prev);
        idsToClear.forEach((id) => {
          next.add(`${id}-hcp`);
          next.add(`${id}-hco`);
          next.add(`${id}-parentHco`);
        });
        return next;
      });

      setExplicitReviewedRecordIds((prev) => {
        const next = new Set(prev);
        idsToClear.forEach((id) => next.delete(id));
        return next;
      });

      if (selectedRecord?.id && idsToClearSet.has(selectedRecord.id)) {
        setSelectedRecord(null);
        setIsSidePanelOpen(false);
      }
    };

    if (mode === "all") {
      skipNextAutoSelectionRef.current = true;
      const allRecordIds = records.map((record) => record.id).filter(Boolean);
      removeRecordSelections(allRecordIds);
      setSelectedRecord(null);
      setIsSidePanelOpen(false);
      return;
    }

    if (mode === "reviewed") {
      const reviewedIdsToClear = records
        .filter(
          (record) =>
            selectedIds.has(record.id) && !isRecordAutoSelected(record, accordionSelected),
        )
        .map((record) => record.id);

      removeRecordSelections(reviewedIdsToClear);
    }
  }, [
    clearSelectionsRequest,
    records,
    selectedIds,
    accordionSelected,
    onSelectionChange,
    setAccordionSelected,
    selectedRecord,
    tabId,
  ]);

  useEffect(() => {
    const requestId = headerSelectionRequest?.id;
    const checked = headerSelectionRequest?.checked;
    const recordIds = Array.isArray(headerSelectionRequest?.recordIds)
      ? headerSelectionRequest.recordIds
      : [];

    if (
      !requestId ||
      requestId === lastHandledHeaderSelectionRequestId.current[tabId]
    ) {
      return;
    }

    lastHandledHeaderSelectionRequestId.current[tabId] = requestId;

    if (recordIds.length === 0) {
      return;
    }

    if (checked) {
      // Header "select all" requests define the exact selection set, not an additive merge.
      const nextSelected = new Set(recordIds);
      const deselectedIds = [...selectedIds].filter(
        (id) => !nextSelected.has(id),
      );

      onSelectionChange(nextSelected);

      if (deselectedIds.length > 0) {
        const nextAccordionSelected = { ...accordionSelected };
        deselectedIds.forEach((id) => {
          delete nextAccordionSelected[`${id}-hcp`];
          delete nextAccordionSelected[`${id}-hco`];
          delete nextAccordionSelected[`${id}-parentHco`];
        });
        setAccordionSelected(nextAccordionSelected);
      }

      setClearedAutoSelectionKeys((prev) => {
        const next = new Set(prev);
        recordIds.forEach((id) => {
          next.delete(`${id}-hcp`);
          next.delete(`${id}-hco`);
          next.delete(`${id}-parentHco`);
        });
        deselectedIds.forEach((id) => {
          next.add(`${id}-hcp`);
          next.add(`${id}-hco`);
          next.add(`${id}-parentHco`);
        });
        return next;
      });

      return;
    }

    const idsToClearSet = new Set(recordIds);
    const nextSelected = new Set(
      [...selectedIds].filter((id) => !idsToClearSet.has(id)),
    );
    const nextAccordionSelected = { ...accordionSelected };
    recordIds.forEach((id) => {
      delete nextAccordionSelected[`${id}-hcp`];
      delete nextAccordionSelected[`${id}-hco`];
      delete nextAccordionSelected[`${id}-parentHco`];
    });

    onSelectionChange(nextSelected);
    setAccordionSelected(nextAccordionSelected);

    setClearedAutoSelectionKeys((prev) => {
      const next = new Set(prev);
      recordIds.forEach((id) => {
        next.add(`${id}-hcp`);
        next.add(`${id}-hco`);
        next.add(`${id}-parentHco`);
      });
      return next;
    });

    setExplicitReviewedRecordIds((prev) => {
      const next = new Set(prev);
      recordIds.forEach((id) => next.delete(id));
      return next;
    });

    if (selectedRecord?.id && idsToClearSet.has(selectedRecord.id)) {
      setSelectedRecord(null);
      setIsSidePanelOpen(false);
    }
  }, [
    headerSelectionRequest,
    selectedIds,
    accordionSelected,
    onSelectionChange,
    setAccordionSelected,
    selectedRecord,
    tabId,
  ]);

  useEffect(() => {
    if (skipNextAutoSelectionRef.current) {
      skipNextAutoSelectionRef.current = false;
      return;
    }

    const nextSelected = new Set(selectedIds);
    let selectedChanged = false;

    records.forEach((record) => {
      const recordId = record?.id;
      if (!recordId || nextSelected.has(recordId)) {
        return;
      }

      const autoSelections = getAutoAccordionSelections(record);
      if (!autoSelections) {
        return;
      }

      const hasEligibleAutoSelection = Object.entries(autoSelections).some(
        ([key, value]) => value && !clearedAutoSelectionKeys.has(key),
      );

      if (hasEligibleAutoSelection) {
        nextSelected.add(recordId);
        selectedChanged = true;
      }
    });

    if (selectedChanged) {
      onSelectionChange(nextSelected);
    }
  }, [records, selectedIds, clearedAutoSelectionKeys, onSelectionChange]);

  useEffect(() => {
    const nextAccordionSelected = { ...accordionSelected };
    const nextExplicitReviewed = new Set(explicitReviewedRecordIds);
    const nextClearedAutoSelectionKeys = new Set(clearedAutoSelectionKeys);
    let accordionChanged = false;
    let explicitChanged = false;
    let clearedChanged = false;

    records.forEach((record) => {
      const recordId = record?.id;
      if (!recordId) {
        return;
      }

      const isSelected = selectedIds.has(recordId);
      if (!isSelected) {
        const keysToClear = [
          `${recordId}-hcp`,
          `${recordId}-hco`,
          `${recordId}-parentHco`,
        ];
        keysToClear.forEach((key) => {
          if (
            Object.prototype.hasOwnProperty.call(nextAccordionSelected, key)
          ) {
            delete nextAccordionSelected[key];
            accordionChanged = true;
          }
        });

        if (nextExplicitReviewed.delete(recordId)) {
          explicitChanged = true;
        }
        return;
      }

      if (nextExplicitReviewed.has(recordId)) {
        return;
      }

      const autoSelections = getAutoAccordionSelections(record);
      if (!autoSelections) {
        return;
      }

      Object.entries(autoSelections).forEach(([key, value]) => {
        if (nextClearedAutoSelectionKeys.has(key)) {
          return;
        }

        if (value && nextAccordionSelected[key] !== value) {
          nextAccordionSelected[key] = value;
          accordionChanged = true;
        }
      });
    });

    if (accordionChanged) {
      setAccordionSelected(nextAccordionSelected);
    }
    if (explicitChanged) {
      setExplicitReviewedRecordIds(nextExplicitReviewed);
    }
    if (clearedChanged) {
      setClearedAutoSelectionKeys(nextClearedAutoSelectionKeys);
    }
  }, [
    records,
    selectedIds,
    accordionSelected,
    explicitReviewedRecordIds,
    clearedAutoSelectionKeys,
    setAccordionSelected,
  ]);

  return (
    <div
      className={`bulk-ui-datatable ${isSidePanelOpen ? "sidepanel-open" : ""}`}
    >
      <DetailSidePanel
        isOpen={isSidePanelOpen}
        onClose={() => setIsSidePanelOpen(false)}
        record={selectedRecord}
        onAccordionSelect={handleAccordionSelect}
        accordionSelected={accordionSelected}
        tabId={tabId}
        onMoveRecord={onMoveRecord}
        onVendorSearch={onVendorSearch}
        isCountryEligibleForVendorSearch={isCountryEligibleForVendorSearch}
      />
      {/* Cards */}
      <div className="space-y-2">
        {paginatedRecords.length === 0 ? (
          <div className="px-6 py-12 text-center text-sm text-muted-foreground bg-white border border-border rounded-lg">
            No records found
          </div>
        ) : (
          paginatedRecords.map((record, index) => {
            // Only disable "Not Found" status records in EPH and Vendor tabs, not in the Not Found tab
            const isRecordDisabledByStatus =
              (tabId !== "notfound" && record.status === "Not Found") ||
              (isLocked && !selectedIds.has(record.id));
            const isExpanded = expandedRows.has(record.id);
            const hasAccordion = record.hasAccordion;
            const isSelectedRecord = selectedRecord?.id === record.id;
            const selectedInHcp = !!accordionSelected[`${record.id}-hcp`];
            const selectedInHco = !!accordionSelected[`${record.id}-hco`];
            const hasMultipleHcp = (record.accordionData?.hcp?.length || 0) > 1;
            const hasMultipleHco = (record.accordionData?.hco?.length || 0) > 1;
            const showVendorSearchActions =
              tabId === "eph" &&
              tabId !== "vendor" &&
              hasMultipleHcp &&
              hasMultipleHco;
            const selectedHcpId = accordionSelected[`${record.id}-hcp`];
            const selectedHcoId = accordionSelected[`${record.id}-hco`];
            const selectedParentHcoId =
              accordionSelected[`${record.id}-parentHco`];
            const selectedHcpRecord = findSelectedAccordionRecord(
              record,
              "hcp",
              selectedHcpId,
            );
            const selectedHcoRecord = findSelectedAccordionRecord(
              record,
              "hco",
              selectedHcoId,
            );
            const selectedParentHcoRecord = findSelectedAccordionRecord(
              record,
              "parentHco",
              selectedParentHcoId,
            );
            const hcpPreviewRecords = buildHcpPreview(
              record,
              selectedHcpId,
              selectedHcoId,
            );
            const hcoPreviewRecords = buildHcoPreview(record, selectedHcoId);
            const primaryHcp = hcpPreviewRecords[0];
            const primaryHco = hcoPreviewRecords[0];

            const defaultColumns = buildDefaultSummaryColumns(
              primaryHcp,
              primaryHco,
            );
            const inputEntities = record.inputEntities || {};
            const hasEntityInput = (entity) =>
              !!entity &&
              typeof entity === "object" &&
              Object.keys(entity).length > 0;
            const hasInputHcp = hasEntityInput(inputEntities.hcp);
            const hasInputHco = hasEntityInput(inputEntities.hco);
            const hasInputParentHco = hasEntityInput(inputEntities.parentHco);
            const hcpResultCount = record.accordionData?.hcp?.length || 0;
            const hcoResultCount = record.accordionData?.hco?.length || 0;
            const parentHcoResultCount =
              record.accordionData?.parentHco?.length || 0;
            const isSingleHcpLayout =
              hasInputHcp && !hasInputHco && !hasInputParentHco;
            const isSingleHcoLayout =
              hasInputHco && !hasInputHcp && !hasInputParentHco;
            const isHcoParentLayout =
              hasInputHco && hasInputParentHco && !hasInputHcp;
            const isHcpParentLayout =
              hasInputHcp && hasInputParentHco && !hasInputHco;
            const useHcoOnLeft = isSingleHcoLayout || isHcoParentLayout;
            const showRightTabForLayout = !(
              isSingleHcpLayout || isSingleHcoLayout
            );
            const useParentHcoOnRight = isHcoParentLayout || isHcpParentLayout;
            const isUniqueRecord = isUniqueResultRecord(record);
            const hasOpenedSidePanel = openedRecordIds.has(record.id);
            const isRecordDisabled =
              tabId === "notfound"
                ? false
                : isRecordDisabledByStatus ||
                  (!isUniqueRecord && !hasOpenedSidePanel);
            const rightVendorType =
              hasInputParentHco && !hasInputHco ? "parent_hco" : "hco";

            let leftSummary = defaultColumns.left;
            let rightSummary = defaultColumns.right;
            let selectedRightRecord = selectedHcoRecord;
            let selectedRightId = selectedHcoId;
            let leftSelectedId = selectedHcpId;
            let leftSelectedRecord = selectedHcpRecord;

            if (tabId === "eph" || tabId === "vendor" || tabId === "notfound") {
              if (hasInputHcp && !hasInputHco && !hasInputParentHco) {
                leftSummary = buildEntitySummaryFromInput(
                  "HCP",
                  inputEntities.hcp,
                );
                rightSummary = null;
              } else if (hasInputHco && !hasInputHcp && !hasInputParentHco) {
                leftSummary = buildEntitySummaryFromInput(
                  "HCO",
                  inputEntities.hco,
                );
                rightSummary = null;
                leftSelectedId = selectedHcoId;
                leftSelectedRecord = selectedHcoRecord;
              } else if (hasInputHcp && hasInputParentHco && !hasInputHco) {
                leftSummary = buildEntitySummaryFromInput(
                  "HCP",
                  inputEntities.hcp,
                );
                rightSummary = buildEntitySummaryFromInput(
                  "PARENT_HCO",
                  inputEntities.parentHco,
                );
                selectedRightRecord = selectedParentHcoRecord;
                selectedRightId = selectedParentHcoId;
              } else if (hasInputHco && hasInputParentHco && !hasInputHcp) {
                leftSummary = buildEntitySummaryFromInput(
                  "HCO",
                  inputEntities.hco,
                );
                rightSummary = buildEntitySummaryFromInput(
                  "PARENT_HCO",
                  inputEntities.parentHco,
                );
                selectedRightRecord = selectedParentHcoRecord;
                selectedRightId = selectedParentHcoId;
                leftSelectedId = selectedHcoId;
                leftSelectedRecord = selectedHcoRecord;
              }
            }

            const leftRenderedRecords = useHcoOnLeft
              ? record.accordionData?.hco || []
              : record.accordionData?.hcp || [];
            const rightRenderedRecords = useParentHcoOnRight
              ? record.accordionData?.parentHco || []
              : record.accordionData?.hco || [];
            const isCheckboxSelected = selectedIds.has(record.id);
            const autoSelectedLeftRecord =
              leftRenderedRecords.length === 1 ? leftRenderedRecords[0] : null;
            const autoSelectedRightRecord =
              rightRenderedRecords.length === 1
                ? rightRenderedRecords[0]
                : null;
            const leftSelectionKey = `${record.id}-${
              useHcoOnLeft ? "hco" : "hcp"
            }`;
            const rightSelectionKey = `${record.id}-${
              useParentHcoOnRight ? "parentHco" : "hco"
            }`;
            const showAutoLeftSelection =
              !!autoSelectedLeftRecord &&
              !clearedAutoSelectionKeys.has(leftSelectionKey);
            const showAutoRightSelection =
              !!autoSelectedRightRecord &&
              !clearedAutoSelectionKeys.has(rightSelectionKey);
            const displayLeftSelectedRecord =
              leftSelectedRecord ||
              (showAutoLeftSelection ? autoSelectedLeftRecord : null);
            const displayRightSelectedRecord =
              selectedRightRecord ||
              (showAutoRightSelection ? autoSelectedRightRecord : null);
            const isExplicitReviewed = explicitReviewedRecordIds.has(record.id);
            const leftSelectionBadgeText =
              isExplicitReviewed && leftSelectedRecord
                ? "Reviewed"
                : displayLeftSelectedRecord
                  ? "Auto Selected"
                  : "";
            const rightSelectionBadgeText =
              isExplicitReviewed && selectedRightRecord
                ? "Reviewed"
                : displayRightSelectedRecord
                  ? "Auto Selected"
                  : "";

            const isHcpSelectedFlag = hasInputHcp
              ? !useHcoOnLeft
                ? !!displayLeftSelectedRecord
                : false
              : false;
            const isHcoSelectedFlag = hasInputHco
              ? useHcoOnLeft
                ? !!displayLeftSelectedRecord
                : !!displayRightSelectedRecord
              : false;
            const isParentHcoSelectedFlag = hasInputParentHco
              ? !!displayRightSelectedRecord
              : false;
            const dcrActionMessage = getDcrActionMessage({
              hasInputHcp,
              hasInputHco,
              hasInputParentHco,
              isHcpSelected: isHcpSelectedFlag,
              isHcoSelected: isHcoSelectedFlag,
              isParentHcoSelected: isParentHcoSelectedFlag,
            });

            return (
              <div key={record.id}>
                {/* Main Card */}
                <div
                  onClick={() => handleRowClick(record)}
                  className={`flex items-stretch gap-3 px-4 py-3 bg-white border border-border rounded transition-all border-left-grid-cs bulk-grid-row-highlight ${
                    isSelectedRecord
                      ? "bulk-grid-row-selected border-l-4 border-l-red-500 shadow-md"
                      : ""
                  } bulk-grid-row-hover ${tabId !== "notfound" ? "cursor-pointer" : "hover:bg-muted/30"}`}
                >
                  {/* Red vertical bar */}
                  {/* <div className="w-1 h-12 bg-red-500 flex-shrink-0 rounded-full"></div> */}

                  {/* Checkbox */}
                  <div
                    className="flex items-start pt-1 flex-shrink-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.has(record.id)}
                      onChange={() => handleSelectRecord(record.id)}
                      disabled={isRecordDisabled}
                      className="w-4 h-4 rounded accent-primary cursor-pointer disabled:cursor-not-allowed"
                    />
                  </div>
                  {/* Left section - DCR ID and Created */}
                  <div className="flex-1 min-w-0 text-xs">
                    <div className="">
                      <div className="font-semibold mb-1">
                        {leftSummary?.label || "HCP"}
                        {leftSummary && leftSelectionBadgeText && (
                          <span
                            className="ml-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold text-green-700 align-middle"
                            style={{ marginLeft: "4px" }}
                          >
                            {leftSelectionBadgeText === "Auto Selected" ? (
                              <AutoModeIcon sx={{ fontSize: "14px" }} />
                            ) : (
                              <span className="right-tick-cs">✓</span>
                            )}
                            <span>{leftSelectionBadgeText}</span>
                          </span>
                        )}
                      </div>
                      {leftSummary ? (
                        <div
                          className="mb-2 last:mb-0"
                          title={leftSummary.hoverDetails || undefined}
                        >
                          {leftSummary.lines.map((line) => (
                            <div
                              key={`${record.id}-${leftSummary.label}-${line.key}`}
                            >
                              <span className="font-medium">{line.key}:</span>{" "}
                              <span
                                title={line.value}
                                className={
                                  line.key === "Enterprise ID" &&
                                  !!displayLeftSelectedRecord
                                    ? "font-bold text-green-700"
                                    : ""
                                }
                              >
                                {compactValue(line.value)}
                              </span>
                            </div>
                          ))}
                          {leftSummary.hiddenCount > 0 && (
                            <Tooltip
                              title={`${leftSummary.hiddenCount} more fields`}
                              placement="top"
                              arrow
                            >
                              <span className="mt-1 inline-flex items-center text-gray-500">
                                <MoreHoriz className="h-4 w-4" />
                              </span>
                            </Tooltip>
                          )}
                        </div>
                      ) : (
                        <div className="text-blue-700">
                          No details available
                        </div>
                      )}
                      {leftSummary && displayLeftSelectedRecord && (
                        <div
                          className="mt-1 inline-flex items-center gap-1 rounded py-0.5 text-[11px] font-semibold text-green-800"
                          style={{ marginBottom: "9px" }}
                        >
                          <span className="right-tick-cs">✓</span>
                          <span>Selected Result:</span>
                          <span
                            style={{ color: "#dc2626", fontWeight: "bold" }}
                          >
                            {getSelectedResultDisplayId(
                              displayLeftSelectedRecord,
                            )}
                          </span>
                        </div>
                      )}
                      {isCheckboxSelected && dcrActionMessage && (
                          <div
                            className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium"
                            style={{ color: "rgb(204, 31, 31)", width: "100%" }}
                          >
                            <InfoOutlinedIcon sx={{ fontSize: "14px" }} />
                            {dcrActionMessage}
                          </div>
                        )}
                    </div>
                  </div>

                  {/* Middle section - Created By */}
                  {/* <div className="flex-1 min-w-0">
                    <div className="text-sm text-gray-900">
                      <span className="font-medium">Created By: </span>
                      <span>{record.name}</span>
                    </div>
                  </div> */}

                  {/* Right section - Country and Assigned To */}
                  {rightSummary && (
                    <div className="flex-1 min-w-0 text-xs">
                      <div className="">
                        <div className="font-semibold mb-1">
                          {rightSummary.label}
                          {(rightSummary.label === "HCO" ||
                            rightSummary.label === "Parent HCO") &&
                            rightSelectionBadgeText && (
                              <span
                                className="ml-2 inline-flex items-center gap-1 rounded-full  px-2 py-0.5 text-[10px] font-semibold text-green-700 align-middle"
                                style={{ marginLeft: "4px" }}
                              >
                                {rightSelectionBadgeText === "Auto Selected" ? (
                                  <AutoModeIcon sx={{ fontSize: "14px" }} />
                                ) : (
                                  <span className="right-tick-cs">✓</span>
                                )}
                                <span>{rightSelectionBadgeText}</span>
                              </span>
                            )}
                        </div>
                        <div
                          className="mb-2 last:mb-0"
                          title={
                            rightSummary.hoverDetails ||
                            (rightSummary.label === "HCO"
                              ? primaryHco?.hoverDetails
                              : undefined)
                          }
                        >
                          {rightSummary.lines.map((line) => (
                            <div
                              key={`${record.id}-${rightSummary.label}-${line.key}`}
                            >
                              <span className="font-medium">{line.key}:</span>{" "}
                              <span
                                title={line.value}
                                className={
                                  (rightSummary.label === "HCO" ||
                                    rightSummary.label === "Parent HCO") &&
                                  line.key === "Enterprise ID" &&
                                  !!displayRightSelectedRecord
                                    ? "font-bold text-green-700"
                                    : ""
                                }
                              >
                                {compactValue(line.value)}
                              </span>
                            </div>
                          ))}
                          {rightSummary.hiddenCount > 0 && (
                            <Tooltip
                              title={`${rightSummary.hiddenCount} more fields`}
                              placement="top"
                              arrow
                            >
                              <span className="mt-1 inline-flex items-center text-gray-500">
                                <MoreHoriz className="h-4 w-4" />
                              </span>
                            </Tooltip>
                          )}
                        </div>
                        {(rightSummary.label === "HCO" ||
                          rightSummary.label === "Parent HCO") &&
                          displayRightSelectedRecord && (
                            <div
                              className="mt-1 inline-flex items-center gap-1 rounded py-0.5 text-[11px] font-semibold text-green-800"
                              style={{ marginBottom: "9px" }}
                            >
                              <span className="right-tick-cs">✓</span>
                              <span>Selected Result:</span>
                              <span
                                style={{ color: "#dc2626", fontWeight: "bold" }}
                              >
                                {getSelectedResultDisplayId(
                                  displayRightSelectedRecord,
                                )}
                              </span>
                            </div>
                          )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Expanded Content - Only for records with accordion */}
                {hasAccordion && showAccordion && isExpanded && (
                  <div
                    className="px-4 py-3 bg-gray-50 border-b border-border"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="mb-3 flex items-center justify-between">
                      <div className="text-sm text-foreground">
                        {record.details && (
                          <span>
                            First Name:{" "}
                            <span className="font-medium">
                              {record.details.firstName || "N/A"}
                            </span>
                            , Last Name:{" "}
                            <span className="font-medium">
                              {record.details.lastName || "N/A"}
                            </span>
                            , Identifier Type:{" "}
                            <span className="font-medium">
                              {record.details.identifierType || "N/A"}
                            </span>
                            , Identifier Id:{" "}
                            <span className="font-medium">
                              {record.details.identifierId || "N/A"}
                            </span>
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary text-white text-xs">
                          2
                        </span>
                      </div>
                    </div>

                    {showVendorSearchActions && (
                      <div className="mb-3 flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            const bothTypes = ["hcp", rightVendorType];
                            onVendorSearch?.(record.id, [
                              ...new Set(bothTypes),
                            ]);
                          }}
                          disabled={
                            selectedInHcp ||
                            selectedInHco ||
                            record.vendorSearchRequested ||
                            record.vendorSearchInProgress
                          }
                          className="px-3 py-1 text-xs font-medium text-indigo-700 bg-white border border-indigo-300 rounded hover:bg-indigo-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          Vendor Search for Both
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onVendorSearch?.(record.id, ["hcp"]);
                          }}
                          disabled={
                            selectedInHcp ||
                            record.vendorSearchRequested ||
                            record.vendorSearchInProgress
                          }
                          className="px-3 py-1 text-xs font-medium text-blue-700 bg-white border border-blue-300 rounded hover:bg-blue-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          Vendor Search HCP
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onVendorSearch?.(record.id, ["hco"]);
                          }}
                          disabled={
                            selectedInHco ||
                            record.vendorSearchRequested ||
                            record.vendorSearchInProgress
                          }
                          className="px-3 py-1 text-xs font-medium text-teal-700 bg-white border border-teal-300 rounded hover:bg-teal-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          Vendor Search HCO
                        </button>
                      </div>
                    )}

                    {/* HCP Accordion */}
                    <div className="mb-3">
                      <div className="bg-white border border-border rounded-lg overflow-hidden">
                        <div
                          className="flex items-center justify-between px-4 py-3 bg-blue-50 hover:bg-blue-100 transition-colors cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleHcpAccordion(record.id);
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-blue-900">
                              HCP
                            </span>
                            <span className="text-xs text-blue-700">
                              ({record.accordionData?.hcp?.length || 0} results)
                            </span>
                          </div>
                          <div
                            className="flex items-center gap-2"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {tabId === "vendor" &&
                              (() => {
                                const selectedInHcp =
                                  !!accordionSelected[`${record.id}-hcp`];
                                return (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onMoveRecord(
                                        record.id,
                                        "vendor",
                                        "notfound",
                                      );
                                    }}
                                    disabled={selectedInHcp}
                                    className="px-3 py-1 text-xs font-medium text-orange-700 bg-white border border-orange-300 rounded hover:bg-orange-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                  >
                                    Raise DCR to Create New
                                  </button>
                                );
                              })()}
                            <button
                              className="p-1"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {expandedHcpAccordions.has(record.id) ? (
                                <KeyboardArrowUp className="w-4 h-4 text-blue-700" />
                              ) : (
                                <KeyboardArrowDown className="w-4 h-4 text-blue-700" />
                              )}
                            </button>
                          </div>
                        </div>
                        {expandedHcpAccordions.has(record.id) && (
                          <div className="p-4 border-t border-border">
                            <div className="border border-border rounded-md overflow-hidden">
                              <div className="grid grid-cols-[50px_1fr_2fr_1.5fr_1fr] gap-4 px-4 py-2 bg-muted border-b border-border text-xs text-foreground font-medium">
                                <div className="flex items-center"></div>
                                <div>
                                  {tabId === "vendor"
                                    ? "OneKey ID"
                                    : "Enterprise ID"}
                                </div>
                                <div>Name</div>
                                <div>Country</div>
                                <div>Status</div>
                              </div>
                              {record.accordionData?.hcp?.map((subRecord) => (
                                <div
                                  key={subRecord.id}
                                  onClick={(e) => e.stopPropagation()}
                                  className={`grid grid-cols-[50px_1fr_2fr_1.5fr_1fr] gap-4 px-4 py-3 text-sm border-b border-border last:border-b-0 hover:bg-muted/30 transition-colors ${
                                    accordionSelected[`${record.id}-hcp`] ===
                                    subRecord.id
                                      ? "bg-blue-50/50"
                                      : ""
                                  }`}
                                >
                                  <div
                                    className="flex items-center"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <input
                                      type="radio"
                                      name={`hcp-accordion-${record.id}`}
                                      checked={
                                        accordionSelected[
                                          `${record.id}-hcp`
                                        ] === subRecord.id
                                      }
                                      onChange={() =>
                                        handleAccordionRadio(
                                          subRecord.id,
                                          record.id,
                                          "hcp",
                                          {
                                            enterpriseId:
                                              subRecord.enterpriseId,
                                            name: subRecord.name,
                                            country: subRecord.country,
                                            status: subRecord.status,
                                          },
                                        )
                                      }
                                      className="w-4 h-4 accent-primary cursor-pointer"
                                    />
                                  </div>
                                  <div>{subRecord.enterpriseId || "NA"}</div>
                                  <div>{subRecord.name}</div>
                                  <div className="text-muted-foreground">
                                    {subRecord.country}
                                  </div>
                                  <div>
                                    {subRecord.status !== "Found in EPH" &&
                                      subRecord.status !==
                                        "Found in Vendor" && (
                                        <span
                                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs ${
                                            subRecord.status === "Not Found"
                                              ? "bg-red-100 text-red-700"
                                              : "bg-gray-100 text-gray-700"
                                          }`}
                                        >
                                          {subRecord.status === "Not Found" && (
                                            <HighlightOff className="w-3 h-3" />
                                          )}
                                          {subRecord.status}
                                        </span>
                                      )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* HCO Accordion */}
                    <div>
                      <div className="bg-white border border-border rounded-lg overflow-hidden">
                        <div
                          className="flex items-center justify-between px-4 py-3 bg-green-50 hover:bg-green-100 transition-colors cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleHcoAccordion(record.id);
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-green-900">
                              HCO
                            </span>
                            <span className="text-xs text-green-700">
                              ({record.accordionData?.hco?.length || 0} results)
                            </span>
                          </div>
                          <div
                            className="flex items-center gap-2"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {tabId === "vendor" &&
                              (() => {
                                const selectedInHco =
                                  !!accordionSelected[`${record.id}-hco`];
                                return (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onMoveRecord(
                                        record.id,
                                        "vendor",
                                        "notfound",
                                      );
                                    }}
                                    disabled={selectedInHco}
                                    className="px-3 py-1 text-xs font-medium text-orange-700 bg-white border border-orange-300 rounded hover:bg-orange-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                  >
                                    Raise DCR to Create New
                                  </button>
                                );
                              })()}
                            <button
                              className="p-1"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {expandedHcoAccordions.has(record.id) ? (
                                <KeyboardArrowUp className="w-4 h-4 text-green-700" />
                              ) : (
                                <KeyboardArrowDown className="w-4 h-4 text-green-700" />
                              )}
                            </button>
                          </div>
                        </div>
                        {expandedHcoAccordions.has(record.id) && (
                          <div className="p-4 border-t border-border">
                            <div className="border border-border rounded-md overflow-hidden">
                              <div className="grid grid-cols-[50px_1fr_2fr_1.5fr_1fr] gap-4 px-4 py-2 bg-muted border-b border-border text-xs text-foreground font-medium">
                                <div className="flex items-center"></div>
                                <div>
                                  {tabId === "vendor"
                                    ? "OneKey ID"
                                    : "Enterprise ID"}
                                </div>
                                <div>Name</div>
                                <div>Country</div>
                                <div>Status</div>
                              </div>
                              {record.accordionData?.hco?.map((subRecord) => (
                                <div
                                  key={subRecord.id}
                                  onClick={(e) => e.stopPropagation()}
                                  className={`grid grid-cols-[50px_1fr_2fr_1.5fr_1fr] gap-4 px-4 py-3 text-sm border-b border-border last:border-b-0 hover:bg-muted/30 transition-colors ${
                                    accordionSelected[`${record.id}-hco`] ===
                                    subRecord.id
                                      ? "bg-blue-50/50"
                                      : ""
                                  }`}
                                >
                                  <div
                                    className="flex items-center"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <input
                                      type="radio"
                                      name={`hco-accordion-${record.id}`}
                                      checked={
                                        accordionSelected[
                                          `${record.id}-hco`
                                        ] === subRecord.id
                                      }
                                      onChange={() =>
                                        handleAccordionRadio(
                                          subRecord.id,
                                          record.id,
                                          "hco",
                                          {
                                            enterpriseId:
                                              subRecord.enterpriseId,
                                            name: subRecord.name,
                                            country: subRecord.country,
                                            status: subRecord.status,
                                          },
                                        )
                                      }
                                      className="w-4 h-4 accent-primary cursor-pointer"
                                    />
                                  </div>
                                  <div>{subRecord.enterpriseId || "NA"}</div>
                                  <div>{subRecord.name}</div>
                                  <div className="text-muted-foreground">
                                    {subRecord.country}
                                  </div>
                                  <div>
                                    {subRecord.status !== "Found in EPH" &&
                                      subRecord.status !==
                                        "Found in Vendor" && (
                                        <span
                                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs ${
                                            subRecord.status === "Not Found"
                                              ? "bg-red-100 text-red-700"
                                              : "bg-gray-100 text-gray-700"
                                          }`}
                                        >
                                          {subRecord.status === "Not Found" && (
                                            <HighlightOff className="w-3 h-3" />
                                          )}
                                          {subRecord.status}
                                        </span>
                                      )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bulk-ui-pagination">
          <div className="bulk-ui-pagination-info">
            Showing {startIndex + 1}-
            {Math.min(startIndex + recordsPerPage, filteredRecords.length)} of{" "}
            {filteredRecords.length}
          </div>
          <div className="bulk-ui-pagination-controls">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="bulk-ui-pagination-btn"
            >
              Previous
            </button>
            <div className="bulk-ui-pagination-numbers">
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let page;
                if (totalPages <= 5) {
                  page = i + 1;
                } else if (currentPage <= 3) {
                  page = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  page = totalPages - 4 + i;
                } else {
                  page = currentPage - 2 + i;
                }
                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`bulk-ui-pagination-number ${
                      currentPage === page ? "active" : ""
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() =>
                setCurrentPage(Math.min(totalPages, currentPage + 1))
              }
              disabled={currentPage === totalPages}
              className="bulk-ui-pagination-btn"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}